import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Search, Plus, Pencil, Trash2, Save, X, RefreshCw, Sparkles,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FABRIC_TABS = [
  { key: "rc-suits",     source: "RC", category: "suits",     label: "RC Suits" },
  { key: "rc-overcoats", source: "RC", category: "overcoats", label: "RC Overcoats" },
  { key: "rc-shirts",    source: "RC", category: "shirts",    label: "RC Shirts" },
  { key: "rc-linings",   source: "RC", category: "linings",   label: "RC Linings" },
  { key: "mx",           source: "MX", category: "suits",     label: "MX Fabrics" },
  { key: "buttons",      source: null, category: null,         label: "Buttons" },
];

const PAGE_SIZE = 50;

const empty = (source, category) => ({
  source, category, rc_code: "", book: "", composition: "", yarn: "",
  weight: "", pattern: "", color: "", price: 0, is_new: false,
});

const emptyButton = (button_category = "suit") => ({
  button_category, code: "", name: "", color: "", material: "", sizes: "", price: 0,
});

const FabricEditor = ({ open, onOpenChange, value, onSubmit, isNew, isButton, existingBooks }) => {
  const [form, setForm] = useState(value);
  const [customBook, setCustomBook] = useState(false);
  useEffect(() => {
    setForm(value);
    // If editing an existing fabric whose book isn't in the dropdown list, show the free-text field
    if (value && value.book && existingBooks && !existingBooks.includes(value.book)) {
      setCustomBook(true);
    } else {
      setCustomBook(false);
    }
  }, [value, existingBooks]);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Add" : "Edit"} {isButton ? "Button" : "Fabric"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {isButton ? (
            <>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Category</label>
                <Select value={form.button_category} onValueChange={(v) => set("button_category", v)}>
                  <SelectTrigger data-testid="form-btn-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suit">Suit</SelectItem>
                    <SelectItem value="overcoat">Overcoat</SelectItem>
                    <SelectItem value="shirt">Shirt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Code *</label>
                <Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="form-btn-code" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Color</label>
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Material</label>
                <Input value={form.material} onChange={(e) => set("material", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Sizes</label>
                <Input value={form.sizes} onChange={(e) => set("sizes", e.target.value)} placeholder="e.g. 32/24L" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Price ($)</label>
                <Input type="number" step="0.01" value={form.price ?? 0} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-xs text-muted-foreground">Source</label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="form-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RC">RC</SelectItem>
                    <SelectItem value="MX">MX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Category</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suits">Suits</SelectItem>
                    <SelectItem value="overcoats">Overcoats</SelectItem>
                    <SelectItem value="shirts">Shirts</SelectItem>
                    <SelectItem value="linings">Linings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground">Fabric Code *</label>
                <Input
                  value={form.rc_code}
                  onChange={(e) => set("rc_code", e.target.value.toUpperCase())}
                  data-testid="form-rc-code"
                  placeholder="e.g. DBK053A"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Book</label>
                {customBook ? (
                  <div className="flex gap-1">
                    <Input
                      value={form.book}
                      onChange={(e) => set("book", e.target.value)}
                      placeholder="New book name"
                      data-testid="form-book-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => { setCustomBook(false); set("book", ""); }}
                      data-testid="form-book-cancel-new"
                      title="Choose from list instead"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={form.book || ""}
                    onValueChange={(v) => {
                      if (v === "__new__") {
                        setCustomBook(true);
                        set("book", "");
                      } else {
                        set("book", v);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="form-book-select">
                      <SelectValue placeholder="Select a book" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(existingBooks || []).map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                      <SelectItem value="__new__" className="text-amber-400">
                        + Add new book…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Color</label>
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Composition</label>
                <Input value={form.composition} onChange={(e) => set("composition", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Pattern</label>
                <Input value={form.pattern} onChange={(e) => set("pattern", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Yarn</label>
                <Input value={form.yarn} onChange={(e) => set("yarn", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Weight</label>
                <Input value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 270g/m" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Price ($/m)</label>
                <Input type="number" step="0.01" value={form.price ?? 0} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
              </div>
              <div className="flex items-center gap-2 mt-5">
                <input type="checkbox" id="is_new" checked={!!form.is_new} onChange={(e) => set("is_new", e.target.checked)} />
                <label htmlFor="is_new" className="text-xs">Mark as NEW</label>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button onClick={() => onSubmit(form)} data-testid="form-save-btn">
            <Save size={14} className="mr-1" /> {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminFabricManagementPage = ({ hideHeader = false } = {}) => {
  const navigate = useNavigate();
  const { reload: reloadCatalog } = useFabricCatalog();
  const [tabKey, setTabKey] = useState("rc-suits");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Edit/create dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorValue, setEditorValue] = useState(null);
  const [editorIsNew, setEditorIsNew] = useState(true);
  const [editorOriginalCode, setEditorOriginalCode] = useState(null);

  const tab = useMemo(() => FABRIC_TABS.find((t) => t.key === tabKey) || FABRIC_TABS[0], [tabKey]);
  const isButton = tab.key === "buttons";

  // Books available per source — used to populate the Book dropdown in the editor.
  const [booksBySource, setBooksBySource] = useState({ RC: [], MX: [] });
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/fabrics/stats`);
        const data = res.data?.books_by_source || {};
        const rcBooks = (data.RC || []).map((b) => b.book).filter(Boolean).sort();
        const mxBooks = (data.MX || []).map((b) => b.book).filter(Boolean).sort();
        if (active) setBooksBySource({ RC: rcBooks, MX: mxBooks });
      } catch {
        if (active) setBooksBySource({ RC: [], MX: [] });
      }
    })();
    return () => { active = false; };
  }, []);

  const existingBooks = useMemo(() => {
    if (isButton) return [];
    const src = (editorValue?.source || tab.source || "RC").toUpperCase();
    return booksBySource[src] || [];
  }, [isButton, editorValue?.source, tab.source, booksBySource]);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    }
  }, [navigate]);

  // Reset pagination when tab or search changes
  useEffect(() => { setPage(0); }, [tabKey, search]);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      if (isButton) {
        const res = await axios.get(`${API}/buttons`, {
          params: { search: search || undefined, limit: PAGE_SIZE, skip: page * PAGE_SIZE },
        });
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        const res = await axios.get(`${API}/fabrics`, {
          params: {
            source: tab.source,
            category: tab.category,
            search: search || undefined,
            limit: PAGE_SIZE,
            skip: page * PAGE_SIZE,
          },
        });
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      }
    } catch (e) {
      toast.error("Failed to fetch");
    } finally {
      setLoading(false);
    }
  }, [isButton, tab.source, tab.category, search, page]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const startAdd = () => {
    setEditorIsNew(true);
    setEditorOriginalCode(null);
    setEditorValue(isButton ? emptyButton() : empty(tab.source, tab.category));
    setEditorOpen(true);
  };

  const startEdit = (row) => {
    setEditorIsNew(false);
    setEditorOriginalCode(isButton ? row.code : row.rc_code);
    setEditorValue({ ...row });
    setEditorOpen(true);
  };

  const submitForm = async (form) => {
    try {
      if (isButton) {
        if (!form.code) return toast.error("Code is required");
        if (editorIsNew) {
          await axios.post(`${API}/buttons`, form);
        } else {
          await axios.put(`${API}/buttons/${encodeURIComponent(editorOriginalCode)}`, form);
        }
      } else {
        if (!form.rc_code) return toast.error("Fabric code is required");
        if (editorIsNew) {
          await axios.post(`${API}/fabrics`, form);
        } else {
          await axios.put(`${API}/fabrics/${encodeURIComponent(editorOriginalCode)}`, form);
        }
      }
      toast.success(editorIsNew ? "Created" : "Saved");
      setEditorOpen(false);
      await fetchList();
      reloadCatalog();
      // Refresh book list (a brand-new book might have been added via "+ Add new book")
      if (!isButton) {
        try {
          const res = await axios.get(`${API}/fabrics/stats`);
          const data = res.data?.books_by_source || {};
          setBooksBySource({
            RC: (data.RC || []).map((b) => b.book).filter(Boolean).sort(),
            MX: (data.MX || []).map((b) => b.book).filter(Boolean).sort(),
          });
        } catch { /* non-fatal */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to save";
      toast.error(detail);
    }
  };

  const deleteRow = async (row) => {
    const code = isButton ? row.code : row.rc_code;
    if (!window.confirm(`Delete ${code}?`)) return;
    try {
      if (isButton) {
        await axios.delete(`${API}/buttons/${encodeURIComponent(code)}`);
      } else {
        await axios.delete(`${API}/fabrics/${encodeURIComponent(code)}`);
      }
      toast.success(`Deleted ${code}`);
      await fetchList();
      reloadCatalog();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to delete";
      toast.error(detail);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={hideHeader ? "" : "min-h-screen pt-20"}>
      {!hideHeader && (
      <section className="border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="fm-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Admin
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
            <div>
              <h1 className="text-2xl font-light flex items-center gap-2">
                <Sparkles size={20} className="text-amber-400" /> Fabric & Button Management
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Add, edit, and remove fabrics and buttons. Changes are live across the whole app.
              </p>
            </div>
            <Button onClick={startAdd} data-testid="fm-add-btn">
              <Plus size={14} className="mr-1" /> Add {isButton ? "Button" : "Fabric"}
            </Button>
          </div>
        </div>
      </section>
      )}

      <section className="py-6">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 border-b border-white/5">
            {FABRIC_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTabKey(t.key)}
                className={`px-3 py-2 text-sm transition-colors ${
                  tabKey === t.key
                    ? "text-amber-400 border-b-2 border-amber-400 -mb-px"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`fm-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Search + count */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={`Search ${tab.label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="fm-search"
              />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="fm-count">
              {loading ? "Loading…" : `${total.toLocaleString()} total`}
            </p>
            <Button variant="ghost" size="sm" onClick={fetchList} disabled={loading}>
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>

          {/* Table */}
          <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  {isButton ? (
                    <>
                      <th className="text-left p-2.5">Code</th>
                      <th className="text-left p-2.5">Name</th>
                      <th className="text-left p-2.5">Category</th>
                      <th className="text-left p-2.5">Color</th>
                      <th className="text-left p-2.5">Material</th>
                      <th className="text-left p-2.5">Sizes</th>
                      <th className="text-right p-2.5">Price</th>
                      <th className="p-2.5 w-24"></th>
                    </>
                  ) : (
                    <>
                      <th className="text-left p-2.5">Code</th>
                      <th className="text-left p-2.5">Book</th>
                      <th className="text-left p-2.5">Composition</th>
                      <th className="text-left p-2.5">Pattern</th>
                      <th className="text-left p-2.5">Color</th>
                      <th className="text-right p-2.5">Price</th>
                      <th className="p-2.5 w-24"></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-xs">No records</td></tr>
                )}
                {items.map((row, idx) => {
                  const code = isButton ? row.code : row.rc_code;
                  return (
                    <motion.tr
                      key={`${code}-${idx}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-t border-white/5 hover:bg-muted/20"
                      data-testid={`fm-row-${code}`}
                    >
                      {isButton ? (
                        <>
                          <td className="p-2.5 font-mono text-xs">{row.code}</td>
                          <td className="p-2.5">{row.name}</td>
                          <td className="p-2.5 capitalize">{row.button_category}</td>
                          <td className="p-2.5">{row.color}</td>
                          <td className="p-2.5">{row.material}</td>
                          <td className="p-2.5 text-xs">{row.sizes}</td>
                          <td className="p-2.5 text-right">${row.price}</td>
                        </>
                      ) : (
                        <>
                          <td className="p-2.5 font-mono text-xs">
                            {row.rc_code}
                            {row.is_new && <span className="ml-2 text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">NEW</span>}
                          </td>
                          <td className="p-2.5 text-xs">{row.book}</td>
                          <td className="p-2.5 text-xs">{row.composition}</td>
                          <td className="p-2.5 text-xs">{row.pattern}</td>
                          <td className="p-2.5 text-xs">{row.color}</td>
                          <td className="p-2.5 text-right">${row.price}</td>
                        </>
                      )}
                      <td className="p-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(row)} data-testid={`fm-edit-${code}`}>
                            <Pencil size={12} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteRow(row)} data-testid={`fm-delete-${code}`}>
                            <Trash2 size={12} className="text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-xs">
              <p className="text-muted-foreground">
                Page {page + 1} of {totalPages} · Showing {items.length} of {total.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="fm-prev-page"
                >Previous</Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                  data-testid="fm-next-page"
                >Next</Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <FabricEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        value={editorValue}
        onSubmit={submitForm}
        isNew={editorIsNew}
        isButton={isButton}
        existingBooks={existingBooks}
      />
    </div>
  );
};

export default AdminFabricManagementPage;
